import { clearApolloStore } from '@/vue-apollo'
import { AUTH_CLEAR } from '@/store/modules/auth/actions'
import logToConsole from '@/utils/consoleHelper'
import { masquerade } from '@/api/auth'
import API from '@/api/apiHelper'

//This defines action constants that can be injecting into components and used to call actions.
export const USER_REQUEST = 'USER_REQUEST'
export const USER_CLEAR = 'USER_CLEAR'
export const USER_LOAD = 'USER_LOAD'
export const USER_CHANGE_PASSWORD = 'USER_CHANGE_PASSWORD'
export const USER_MASQUERADE_REQUEST = 'USER_MASQUERADE_REQUEST'
export const USER_STOP_MASQUERADING_REQUEST = 'USER_STOP_MASQUERADING_REQUEST'

export default {
  /**
   * This action is used for requesting the logged in users profile from the graphql gateway
   *
   * @returns {Promise<unknown>}
   */
  [USER_REQUEST]: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('USER_REQUESTED')
      API.query(
        `
        query Query {
          me {
            id
            first_name
            last_name
            full_name
            email
            contact_number
            account {
              id
              name
            }
            account_type
            active
            username
            parent_id
            admin
            super_admin
            has_team
            teams {
              id
              ref
              name
            }
            permissions {
              access {
                ref
              }
            }
          }
        }
      `
      )
        .then(userProfile => {
          if (userProfile == null || typeof userProfile === 'undefined') {
            return reject('Unable to load user profile')
          }
          return resolve(userProfile)
        })
        .catch(error => {
          commit('USER_FAILURE')
          return reject(error)
        })
    })
  },
  /**
   *  This action is used for initiating the load of the logged in user
   *
   *  @returns {Promise<unknown>}
   */
  [USER_LOAD]: ({ state, commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      logToConsole('Load user requested', 'debug')
      if (state.loaded !== true) {
        //if user payload is present in the local-storage load parse it and load it.
        let user = localStorage.getItem('user_profile') || false
        try {
          if (user !== false) {
            logToConsole('Loading user profile from storage', 'debug')
            commit('USER_LOAD', JSON.parse(user))
            commit('USER_SUCCESS')
            return resolve()
          }
        } catch (e) {
          logToConsole(
            'Error reading stored user profile, fetching fresh profile',
            'debug'
          )
        }
        //otherwise fetch a new one.
        dispatch(USER_REQUEST)
          .then(user => {
            commit('USER_LOAD', user)
            commit('USER_SUCCESS')
            commit('USER_SAVE', user)
            return resolve()
          })
          .catch(error => {
            commit('USER_FAILURE')
            dispatch(AUTH_CLEAR)
              .then(() => {
                return reject(error)
              })
              .catch(error => {
                return reject(error)
              })
          })
      } else {
        logToConsole('User is already loaded', 'debug')
        return resolve()
      }
    })
  },
  /**
   *  This action is used to clear the logged in user context
   *
   *  @returns {Promise<unknown>}
   */
  [USER_CLEAR]: ({ commit }) => {
    return new Promise(resolve => {
      commit('USER_RESET')
      return resolve()
    })
  },
  /**
   *  This action is used for the current logged in user to change their
   *  password, this is separate from the reset password
   *
   *  @returns {Promise<unknown>}
   */
  [USER_CHANGE_PASSWORD]: ({ commit }) => {
    return new Promise(resolve => {
      commit('USER_PASSWORD_CHANGED')
      return resolve()
    })
  },
  /**
   *  This action is used for admins to masquerade as other users
   *
   *  @param {Object} user - The user who we are going to masquerade as.
   *  @param {string} user.user_id - The ID of the user we are masquerading as.
   *
   *  @returns {Promise<unknown>}
   */
  [USER_MASQUERADE_REQUEST]: ({ commit, dispatch, rootGetters }, user) => {
    return new Promise((resolve, reject) => {
      commit('USER_MASQUERADE_REQUESTED', user.user_id)
      return masquerade(user.user_id)
        .then(() => {
          commit('USER_MASQUERADE_SUCCESS')
          commit('USER_RESET')
          logToConsole('Running USER_LOAD', 'debug')
          dispatch(USER_LOAD)
            .then(() => {
              logToConsole('Clearing Apollo cache', 'debug')
              clearApolloStore().then(() => {
                logToConsole('Apollo cache cleared', 'debug')
              })
              commit('CHECK_IF_USER_IS_MASQUERADING', rootGetters.getAuthUserID)
              return resolve()
            })
            .catch(error => {
              return reject(error)
            })
        })
        .catch(error => {
          commit('USER_MASQUERADE_FAILURE', user.user_id)
          return reject(error)
        })
    })
  },
  /**
   *  This action is used to cancel the current masquerade and
   *  return to the authenticated user
   *
   *  @returns {Promise<unknown>}
   */
  [USER_STOP_MASQUERADING_REQUEST]: ({ getters, dispatch }) => {
    return dispatch(USER_MASQUERADE_REQUEST, { user_id: getters.getAuthUserID })
  }
}
